import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistance } from 'date-fns';

import UploadIcon from '@mui/icons-material/CloudUploadOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteEmptyIcon from '@mui/icons-material/FavoriteBorder';
import ReplyIcon from '@mui/icons-material/Reply';
import { Box, MenuItem, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import MoreVerticalIcon from '@mui/icons-material/MoreVert';

import AppEntityAvatar, {
  AvatarSize,
} from '../../../designe/app-entity-avatar/AppEntityAvatar';
import AppMenu, {
  getPosition,
  PositionOption,
} from '../../../designe/app-menu/AppMenu';
import AppTextField from '../../../designe/app-text-field/AppTextField';
import AppFile from '../../../designe/app-file/AppFile';
import AppButton from '../../../designe/app-button/AppButton';
import { useStyles } from './Comment.styles';
import theme from '../../../../styles/theme';
import { SvgClass } from '../../../../styles/mixins';
import { useComment } from './useComment';
import { IComment } from '../../../../types/interfaces/comment';

interface CommentProps {
  comment: IComment;
  repliedComment?: boolean;
  onCommentReply?: (comment: IComment) => void;
  onCommentDeleteFromReply?: () => void;
  allowReply?: boolean;
}

export default function Comment(props: CommentProps) {
  const {
    comment,
    repliedComment,
    onCommentReply,
    onCommentDeleteFromReply,
    allowReply = true,
  } = props;
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const {
    onCommentRemove,
    onCommentSave,
    onCommentReaction,
    onCommentEdit,
    onToggleToEditComment,
    editedComment,
    commentFiles,
    onUploadFiles,
    onDeleteFile,
    userReaction,
    user,
  } = useComment(comment);

  function getCommentReactionIcon() {
    return userReaction ? (
      <FavoriteIcon
        onClick={() => onCommentReaction()}
        className={SvgClass.active}
      />
    ) : (
      <FavoriteEmptyIcon onClick={() => onCommentReaction()} />
    );
  }

  return (
    <div className={classes.commentWrapper}>
      <div className={classes.left}>
        <AppEntityAvatar entity={comment.owner} size={AvatarSize.m} />
      </div>
      <div className={classes.right}>
        <div className={classes.header}>
          <div className={classes.userInfo}>
            <Typography variant="text-lg">
              {`${comment.owner.firstname} ${comment.owner.lastname}`}
            </Typography>
            <Typography variant="text-sm" className={classes.createdAt}>
              {formatDistance(comment.createdAt, new Date(), {
                addSuffix: true,
              })}
            </Typography>
          </div>

          <div>
            {!repliedComment &&
              !editedComment &&
              (comment.ownerId === user?.id ||
                comment.pageUserId === user?.id) && (
                <AppMenu
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={getPosition(
                    PositionOption.bottom,
                    PositionOption.right,
                  )}
                  transformOrigin={getPosition(
                    PositionOption.top,
                    PositionOption.right,
                  )}
                  targetElement={
                    <MoreVerticalIcon
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                    />
                  }>
                  {comment.ownerId === user?.id && (
                    <MenuItem
                      onClick={() => {
                        onToggleToEditComment();
                        setAnchorEl(null);
                      }}>
                      <EditIcon />
                      {t('comment.editComment')}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      onCommentRemove();
                      setAnchorEl(null);
                    }}>
                    <DeleteIcon />
                    {t('comment.removeComment')}
                  </MenuItem>
                </AppMenu>
              )}
            {repliedComment && onCommentDeleteFromReply && (
              <CloseIcon onClick={() => onCommentDeleteFromReply()} />
            )}
          </div>
        </div>
        <Box mt={1} ml={1}>
          {!editedComment && (
            <Typography variant="text-md">{comment.content}</Typography>
          )}
          {editedComment && (
            <div className={classes.editedCommentContent}>
              <AppTextField
                fullWidth
                multiline
                maxRows={5}
                value={editedComment.content}
                onChange={(e) => onCommentEdit(e.target.value)}
              />
              {!!commentFiles?.length && (
                <div className={classes.filesWrapper}>
                  {commentFiles.map((file) => (
                    <AppFile
                      key={file.id}
                      file={file}
                      onDelete={(file) => onDeleteFile(file.id)}
                    />
                  ))}
                </div>
              )}
              <div className={classes.editedCommentBottom}>
                <AppButton
                  variant="outlined"
                  component="label"
                  className={classes.uploadFileButton}>
                  <input
                    autoComplete="off"
                    multiple
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    type="file"
                    onChange={(e) => onUploadFiles(e.target.files)}
                  />
                  <UploadIcon />
                  {t('home.uploadFiles')}
                </AppButton>

                <AppButton
                  onClick={onCommentSave}
                  disabled={
                    !editedComment.content && !editedComment?.files?.length
                  }>
                  {t('common.save')}
                </AppButton>
              </div>
            </div>
          )}

          {!editedComment && !!commentFiles.length && (
            <div className={classes.filesWrapper}>
              {commentFiles.map((file) => (
                <AppFile key={file.id} file={file} />
              ))}
            </div>
          )}
          {comment.parent && !repliedComment && (
            <div className={classes.repliedCommentWrapper}>
              <Comment comment={comment.parent} repliedComment />
            </div>
          )}
        </Box>
        {!repliedComment && onCommentReply && (
          <div className={classes.footer}>
            {getCommentReactionIcon()}
            {allowReply && (
              <ReplyIcon onClick={() => onCommentReply(comment)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
